import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  ConstructionAreas,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { StyledDivider } from '../styles';

interface HouseConstructionAreasProps {
  formData: ConstructionAreas;
  sectionStatus?: SectionStatus;
}

export const HouseConstructionAreasSection = memo(
  function HouseConstructionAreasSection({
    formData,
    sectionStatus,
  }: HouseConstructionAreasProps) {
    return (
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <SectionStatusBadge status={sectionStatus} />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="construction-areas-construcao"
          >
            Áreas de Construção
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight={600}>Averbada</Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa da Unidade
              </Typography>
              <Typography>
                {formData.registered_areas.private_unit ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa Descontínua
              </Typography>
              <Typography>
                {formData.registered_areas.private_discontinuous ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Áreas Comuns
              </Typography>
              <Typography>
                {formData.registered_areas.common_areas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total Averbada
              </Typography>
              <Typography>{formData.registered_areas.total ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight={600}>Não Averbadas</Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa da Unidade
              </Typography>
              <Typography>
                {formData.unregistered_areas.private_unit ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa Descontínua
              </Typography>
              <Typography>
                {formData.unregistered_areas.private_discontinuous ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Áreas Comuns
              </Typography>
              <Typography>
                {formData.unregistered_areas.common_areas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total Não Averbada
              </Typography>
              <Typography>
                {formData.unregistered_areas.total ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography fontWeight={600}>
            Totais (Averbadas e Não Averbadas)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa da Unidade
              </Typography>
              <Typography>
                {formData.total_areas.private_unit ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Privativa Descontínua
              </Typography>
              <Typography>
                {formData.total_areas.private_discontinuous ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Áreas Comuns
              </Typography>
              <Typography>
                {formData.total_areas.common_areas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total Averbada
              </Typography>
              <Typography>{formData.total_areas.total ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="construction-privativa-descoberta"
          >
            Privativa Descoberta
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Área
              </Typography>
              <Typography>{formData.private_uncovered.area ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="construction-divisao-interna"
          >
            Divisão Interna
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Áreas Sociais</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Sala(s)
              </Typography>
              <Typography>
                {formData.internal_division.social_areas.living_rooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Hall(s)/Circulação(ões)
              </Typography>
              <Typography>
                {formData.internal_division.social_areas.halls_or_circulation ??
                  '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Sacada(s)/Varanda(s)
              </Typography>
              <Typography>
                {formData.internal_division.social_areas
                  .balconies_or_verandas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Terraço(s)
              </Typography>
              <Typography>
                {formData.internal_division.social_areas.terraces ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Mezanino(s)
              </Typography>
              <Typography>
                {formData.internal_division.social_areas.mezzanines ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Escritório/Gabinete
              </Typography>
              <Typography>
                {formData.internal_division.others.office_or_study ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Áreas íntimas</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Quarto(s)
              </Typography>
              <Typography>
                {formData.internal_division.intimate_areas.bedrooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Quarto(s) de Empregada
              </Typography>
              <Typography>
                {formData.internal_division.intimate_areas.maid_bedrooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Closet(s)
              </Typography>
              <Typography>
                {formData.internal_division.intimate_areas.closets ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Cozinha e Serviços</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cozinha(s)
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services.kitchens ??
                  '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cozinha Americana
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services
                  .american_kitchen ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Copa(s)
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services.pantries ??
                  '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Copa/Cozinha Conjugadas
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services
                  .combined_pantry_kitchen ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cozinha/Área de Serviço Conjugadas
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services
                  .combined_kitchen_service ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Área(s) de Serviço
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services
                  .service_areas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Área(s) de Serviço Descobertas
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services
                  .uncovered_service_areas ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Despensa(s)/Depósito(s)
              </Typography>
              <Typography>
                {formData.internal_division.kitchen_and_services.storerooms ??
                  '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Banheiros</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Banheiro(s) Sociai(s)
              </Typography>
              <Typography>
                {formData.internal_division.bathrooms.social_bathrooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Banheiro(s) Privativo(s)
              </Typography>
              <Typography>
                {formData.internal_division.bathrooms.private_bathrooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Banheiro(s) de Serviço
              </Typography>
              <Typography>
                {formData.internal_division.bathrooms.service_bathrooms ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Banheiro Privativo de Empregada
              </Typography>
              <Typography>
                {formData.internal_division.bathrooms.maid_private_bathroom ??
                  '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Lavabo(s)
              </Typography>
              <Typography>
                {formData.internal_division.bathrooms.lavatories ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography fontWeight={600}>Outros</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Outros
              </Typography>
              <Typography>
                {formData.internal_division.others.others ?? '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
